<template>
  <div class="prod-swiper__wrapper">
    <!-- Swiper image slider section -->
    <div ref="productSlider" class="prod-swiper__swiper-container swiper-container">
      <div class="prod-swiper__swiper-cards">
        <button
          v-for="(product, index) in products"
          :key="index"
          :class="[
            'prod-swiper__swiper-card',
            { OOS: !product.available && showOOSbadge && !hasBISTag(product) },
            { selected: isSelected(product.id) },
          ]"
          :data-stio-gtm-click-id="gtmTrackId != '' ? gtmTrackId : null"
          :data-ctl-click="isTrackClickEvent"
          @click="handleProductCardClick(product, false, index)"
        >
          <!-- Check mark circle icon on selected product image -->
          <svg v-if="isSelected(product.id)" class="prod-swiper__check-mark-icon">
            <use xlink:href="#check-mark-circle"></use>
          </svg>

          <div v-if="!product?.available && showOOSbadge && !hasBISTag(product)" class="prod-swiper__oos-badge">SOLD OUT</div>

          <transition name="fade" mode="out-in">
            <ImageField
              class="prod-swiper__feature-img"
              :obj="{ url: featureImage?.[product.id]?.src, alt: product?.title, width: 600, height: 750 }"
              :isLoadingLazy="isLoadingLazy"
              :key="featureImage?.[product.id]?.src"
            />
          </transition>
        </button>
      </div>
    </div>

    <!-- Product info section, it is non-swiper -->
    <div
      v-if="bykSelectedProducts?.[0]"
      :class="['prod-swiper__info', { 'prod-swiper__info--secondTurn': isSecondTurn }]"
    >
      <!-- Title -->
      <div
        :class="[
          'prod-swiper__product-title',
          { 'prod-swiper__product-title--margin': !selectedColorMap[bykSelectedProducts?.[0]?.id] },
        ]"
      >
        {{ bykSelectedProducts?.[0]?.title }}
      </div>

      <!-- Swatches -->
      <div v-if="!isSecondTurn" class="prod-swiper__swatch-container">
        <div class="prod-swiper__swatch-title">
          <span>COLOR</span>
          <div class="prod-swiper__swatch-title--selected">
            <span>{{ selectedColorMap[bykSelectedProducts?.[0]?.id] }}</span>
          </div>
        </div>

        <div v-if="selectedColorMap[bykSelectedProducts?.[0]?.id]" class="prod-swiper__swatches">
          <!-- prev icon -->
          <div
            v-if="!disableScrollIcon"
            :class="['prod-swiper__swatches-prev', { 'prod-swiper__swatches--blur': leftScroll === 0 }]"
            @click="scrollSwatches('left')"
          >
            <svg class="prod-swiper__swatches-prev-icon">
              <use xlink:href="#icon-prev"></use>
            </svg>
          </div>

          <!-- swatches color section -->
          <div ref="swatchWrapper" class="prod-swiper__swatches-wrapper">
            <div
              tabindex="0"
              v-for="(value, name) in renderColorOptions[bykSelectedProducts?.[0]?.id]"
              :key="name"
              :class="[
                'prod-swiper__swatchButton',
                { active: name === selectedColorMap[bykSelectedProducts?.[0]?.id] },
              ]"
              @click="(e) => switchActiveSwatch(e, name)"
              @keydown="(e) => handleKeyDownOnSwatches(e, name)"
            >
              <ImageField
                v-if="value.image"
                class="prod-swiper__swatchImage"
                :obj="{ url: value.image.src, alt: value.image.alt, width: 32, height: 32 }"
                :isLoadingLazy="isLoadingLazy"
              />
            </div>
          </div>

          <!-- next icon -->
          <div
            v-if="!disableScrollIcon"
            :class="['prod-swiper__swatches-next', { 'prod-swiper__swatches--blur': leftScroll === rightScroll }]"
            @click="scrollSwatches('right')"
          >
            <svg class="prod-swiper__swatches-next-icon">
              <use xlink:href="#icon-next"></use>
            </svg>
          </div>
        </div>
      </div>

      <!-- Price -->
      <div class="prod-swiper__price-wrapper">
        <!-- <ProductPrice
          :product="bykSelectedProducts?.[0]"
          :selectedFirstOption="{
            price: bykSelectedProducts?.[0]?.selectedVariantDetails?.price,
            compare_at_price: bykSelectedProducts?.[0]?.selectedVariantDetails?.comparePrice,
          }"
        /> -->
        <div class="pc__price-wrapper">
          <div :class="['pc__price', { bold: getComparePrice(bykSelectedProducts?.[0]) > getPrice(bykSelectedProducts?.[0]) }]">
            <span v-if="isSecondTurn">Used</span> {{ formatPrice(getPrice(bykSelectedProducts?.[0])) }}
          </div>
          <div v-if="isPriceRange(bykSelectedProducts?.[0]) && getCustomerDiscount(bykSelectedProducts?.[0]) <= 0" class="pc__price-range">{{ isPriceRange(bykSelectedProducts?.[0]) }}</div>
          <div v-else-if="getComparePrice(bykSelectedProducts?.[0]) > getPrice(bykSelectedProducts?.[0])" class="pc__compare-price">
            {{ formatPrice(getComparePrice(bykSelectedProducts?.[0])) }}
          </div>
        </div>
      </div>

      <!-- CTA -->
      <button class="btn btn--regular prod-swiper__product-details-cta" @click="handleViewProductDetailsClick">
        View product details
      </button>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted, toRefs, ref, onBeforeMount, reactive, watch } from 'vue';
  import ImageField from '../../global/image.vue';
  import useWindowResize from '../../../utils/use-window-resize.js';
  import ProductPrice from '../../pdp-first-fold/components/product-price.vue';
  import Swiper from 'swiper';
  import SwiperCore, { Navigation, EffectFade, Thumbs, Pagination, A11y } from 'swiper/core';

  SwiperCore.use([Navigation, EffectFade, Thumbs, Pagination, A11y]);

  const props = defineProps({
    isSecondTurn: {
      type: Boolean,
      default: false,
    },
    showOOSbadge: {
      type: Boolean,
      default: false,
    },
    gtmTrackId: {
      type: String,
      default: '',
    },
    isLoadingLazy: {
      type: Boolean,
      default: false,
    },
    isTrackClickEvent: {
      type: Boolean,
      default: false,
    },
    selectedProducts: {
      type: Object,
      default: () => ({}),
    },
    layer: String, // 'outer_layer', 'mid_layer', or 'base_layer'
    subcategory: String, // 'top' or 'bottom',
    products: {
      type: Array,
      required: true,
    },
    onViewProductDetailsClick: {
      type: Function,
    },
  });

  const { isSecondTurn, layer, subcategory, products } = toRefs(props);
  const emit = defineEmits(['product-click', 'view-product-details']);

  const selectedProductsObject = reactive(props.selectedProducts);
  const selectedColorMap = ref({});
  const swatchWrapper = ref(null);
  const leftScroll = ref(null);
  const rightScroll = ref(null);
  const { windowWidth } = useWindowResize();

  const productSlider = ref(null);

  onBeforeMount(() => {
    initialSetup();
  });

  onMounted(() => {
    new Swiper(productSlider.value, productCarousel.value);
  });

  const hasBISTag = (product) => {
    return product?.tags?.some((tag) => tag.toLowerCase().startsWith('bis:'));
  };

  const isSelected = (id) => {
    return bykSelectedProducts.value ? bykSelectedProducts?.value?.[0]?.id === id : false;
  };

  const initialSetup = () => {
    if (renderColorOptions.value) {
      for (let prod of products.value) {
        if (prod?.options?.[0]?.name === 'Color') {
          selectedColorMap.value[prod.id] =
            getLastSelectedSwatch(prod.id) || Object.keys(renderColorOptions.value?.[prod.id])[0];
        }
      }
    }

    setTimeout(() => {
      if (swatchWrapper.value) {
        leftScroll.value = swatchWrapper.value.scrollLeft;
        rightScroll.value = swatchWrapper.value.scrollWidth - swatchWrapper.value.offsetWidth;
      }
    }, 200);
  };

  const productCarousel = computed(() => {
    return {
      slidesPerView: 3.3,
      spaceBetween: 12,
      wrapperClass: 'prod-swiper__swiper-cards',
      slideClass: 'prod-swiper__swiper-card',
      initialSlide: bykSelectedProducts?.value?.[0]?.selectedVariantDetails?.productIndexForSwiper || 0,
      breakpoints: {
        768: {
          slidesPerView: 4.3,
          spaceBetween: 16,
        },
      },
      a11y: {
        enabled: true,
      },
    };
  });

  const bykSelectedProducts = computed(() => {
    return selectedProductsObject?.[layer.value]?.[subcategory.value] || [];
  });

  const featureImage = computed(() => {
    const productImagesMap = {};

    for (let item of products.value) {
      const { images, title, id } = item;
      const imageData = window.getProductImages(images, selectedColorMap.value?.[item.id], title);
      productImagesMap[id] = imageData[0];
    }

    return productImagesMap;
  });

  const renderColorOptions = computed(() => {
    const colorOptionsMap = {};
    const allProducts = products.value;

    if (!Boolean(allProducts.length)) {
      return colorOptionsMap;
    }

    const getColorOption = (selProd) => {
      const { variants, tags, images, available } = selProd;

      const firstLevelKeys = variants.reduce((set, variant) => {
        const property = variant.option1;
        const setItem = (set[property] = set[property] || { items: [] });
        setItem.items.push(variant);
        if (variant.available) setItem.items.available = true;
        return set;
      }, {});

      Object.entries(firstLevelKeys).forEach(([key, value]) => {
        const isAvailable = value.items.available;
        const bisData = tags.filter((tag) => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

        if (isAvailable || bisData.length || !available) {
          const imagePattern = new RegExp(`^\\s*${key.toLowerCase()}\\s*swatch\\s*$`);
          value.image = images.filter((image) => {
            const alt = image.alt.toLowerCase();
            return alt && imagePattern.test(alt);
          })[0];

          value.price = Number(value.items[0].price / 100);
          value.comparePrice = Number(value.items[0].compare_at_price / 100);
        } else {
          delete firstLevelKeys[key];
        }
      });

      return firstLevelKeys;
    };

    for (let prod of allProducts) {
      colorOptionsMap[prod.id] = getColorOption(prod);
    }

    return colorOptionsMap;
  });

  const disableScrollIcon = computed(() => {
    if (swatchWrapper.value) {
      const isScroll = swatchWrapper.value.offsetWidth === swatchWrapper.value.scrollWidth;
      return windowWidth.value > 1023 ? isScroll : isScroll;
    }
  });

  const formatPrice = (value) => {
    return window.formattedPrice(value);
  };

  const isPriceRange = (product) => {    
    const { min_price, max_price } = product;
    if (min_price == max_price) return false;

    return `(${formatPrice(min_price / 100)} - ${formatPrice(max_price / 100)})`;
  };

  const activeColorData = (selProduct) => {
    return (
      selectedColorMap.value?.[selProduct?.id] &&
      renderColorOptions.value[selProduct?.id][selectedColorMap.value?.[selProduct?.id]]
    );
  };

  const getLastSelectedSwatch = (productId) => {
    let bykSelectedColor;
    if (productId === bykSelectedProducts.value?.[0]?.id) {
      bykSelectedColor = bykSelectedProducts.value?.[0]?.selectedVariantDetails?.color || '';
    }

    return bykSelectedColor;
  };

  const getPrice = (selProduct) => {
    const activeColorDataResult = activeColorData(selProduct);
    const customerDiscountVal = getCustomerDiscount(selProduct);

    if (activeColorDataResult && activeColorDataResult.comparePrice > 0) {
      const { comparePrice, price } = activeColorDataResult;

      if (comparePrice * (1 - customerDiscountVal) < price) {
        return comparePrice * (1 - customerDiscountVal);
      } else {
        return price;
      }
    } else {
      return (selProduct?.price / 100) * (1 - customerDiscountVal);
    }
  };

  const getComparePrice = (selProduct) => {
    const activeColorDataResult = activeColorData(selProduct);
    if (activeColorDataResult) {
      if (activeColorDataResult.comparePrice > 0) {
        return activeColorDataResult.comparePrice;
      } else {
        return activeColorDataResult.price;
      }
    } else {
      return selProduct?.compare_at_price / 100;
    }
  };

  const switchActiveSwatch = (e, name) => {
    e.preventDefault();
    selectedColorMap.value[bykSelectedProducts?.value?.[0]?.id] = name;
    handleProductCardClick(
      bykSelectedProducts?.value?.[0],
      true,
      bykSelectedProducts?.value?.[0]?.selectedVariantDetails?.productIndexForSwiper
    );
  };

  const scrollSwatches = (pos) => {
    const element = swatchWrapper.value;
    if (pos === 'left') {
      element.scrollLeft -= 100;
    } else {
      element.scrollLeft += 100;
    }

    leftScroll.value = element.scrollLeft;
    rightScroll.value = element.scrollWidth - element.offsetWidth;
  };

  const getCustomerDiscount = (product) => {
    return window.customerDiscount(product?.tags || []);
  };

  const handleKeyDownOnSwatches = (event, name) => {
    if (event.key === 'Enter' || event.key === ' ') {
      switchActiveSwatch(event, name);
    }
  };

  const handleProductCardClick = (prod = {}, selectedUsingSwatch = false, productIndex = 0) => {
    const updatedSelectedProduct = {
      ...prod,
      selectedVariantDetails: {
        selectedFeatureImage: featureImage.value?.[prod?.id],
        color: selectedColorMap.value?.[prod?.id],
        price: getPrice(prod),
        comparePrice: getComparePrice(prod),
        productIndexForSwiper: productIndex,
      },
    };

    let productToEmit;

    if (selectedUsingSwatch) {
      // Always dispatch the updated product when using swatch
      productToEmit = updatedSelectedProduct;
    } else {
      // When not using swatch, dispatch undefined if selected to deselect the product, else dispatch the updated product
      productToEmit = isSelected(prod?.id) ? undefined : updatedSelectedProduct;
    }

    emit('product-click', {
      product: productToEmit,
      layer: layer.value,
      subcategory: subcategory.value,
    });
  };

  const handleViewProductDetailsClick = () => {
    emit('view-product-details', {
      product: bykSelectedProducts?.value?.[0],
      selectedColor: selectedColorMap.value?.[bykSelectedProducts?.value?.[0]?.id],
      layer: layer.value,
      subcategory: subcategory.value,
    });
  };

  watch(bykSelectedProducts, () => {
    swatchWrapper.value = null;
  });
</script>
